import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'

import {
  PropertiesLoader,
  PropertyCard,
  PropertyList,
  PropertyListPaginator
} from '@wiggot/search-components/properties-library/src'

import {
  ZoneInformationBySearchAccordion,
  ZoneInformationBySearchLoader
} from '@wiggot/search-components/insights-library/src'
import {
  useFilterStore,
  usePropertiesStore,
  useZoneInformationStore
} from '@wiggot/search-components/state-library/src'

import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider'
import { composeClasses } from '@wiggot/utils/src/strings'
import useMatchWindowQuery from '@wiggot/web-components/hooks/ds/useMatchWindowQuery'
import { numberWithCommaWithoutDecimals } from '@wiggot/web-components/utils/formatterNumbers'
import GoogleIcon from '@dd3tech/wiggui/GoogleIcon'
import Transition from '@dd3tech/wiggui/Transition'

import { useSaveSearch } from '@/hooks/useSaveSearch'
import { AmplitudeEvents } from '@/interfaces/enum'
import { propertyInteraction } from '@/services/interaction.services'
import { useSession } from '@wiggot/auth/hooks/useSession'
import { CardFooter, CardHeader } from './Card'
import GridLoader from './GridLoader'
import { ListHeader } from './ListHeader'

import MonopolioIcon from '@/assets/svg/monopolio-icon.svg'
import MonopolioLetters from '@/assets/svg/monopolio-letters.svg'

export const Properties = ({
  onCleanFilters,
  onShowMap,
  hiddeCards,
  hiddeMap,
  isShowTour,
  mapboxGLIsSupported
}: {
  onCleanFilters?: () => void
  onShowMap: () => void
  hiddeCards: boolean
  mapboxGLIsSupported: boolean
  hiddeMap: boolean
  isShowTour: boolean
}) => {
  const { showOnlyCommissionProperties, locations } = useFilterStore(
    (state) => state
  )
  const { zone: data } = useZoneInformationStore()
  const { properties, propertyLoaderStatus } = usePropertiesStore()
  const { profile } = useSession()
  const zone = useMemo<{
    avgPrice?: string
    avgM2Price?: string
    avgSurface?: string
  }>(() => {
    return {
      avgPrice: numberWithCommaWithoutDecimals(data?.avgPrice ?? 0),
      avgM2Price: numberWithCommaWithoutDecimals(data?.avgM2Price ?? 0),
      avgSurface: numberWithCommaWithoutDecimals(data?.avgSurface ?? 0)
    }
  }, [data])
  const { trackEvent, isInitialized } = useAmplitude()
  const { isMD } = useMatchWindowQuery()
  const { saveSearch } = useSaveSearch()
  const [flyToMapPropertyId, setFlyToMapPropertyId] = useState('')
  const [showSideInfo, setShowSideInfo] = useState(false)
  const [noDisplaySideInfo, setNoDisplaySideInfo] = useState(true)
  const [idHover, setIdHover] = useState('')
  const onFlyToMap = (propertyId: string) => {
    setFlyToMapPropertyId(propertyId)
  }
  const onChangeShowSideInfo = () => {
    setShowSideInfo((prev) => !prev)
  }
  useEffect(() => {
    if (!hiddeMap) {
      setShowSideInfo(false)
    }
  }, [hiddeMap])

  useEffect(() => {
    if (!showSideInfo) {
      setTimeout(() => {
        setNoDisplaySideInfo(true)
      }, 150)
      return
    }
    setNoDisplaySideInfo(false)
  }, [showSideInfo])

  useEffect(() => {
    if (!propertyLoaderStatus?.loading) {
      const wiggotProperties = properties
        ?.filter((property) => property?.isWiggotProperty)
        ?.map((property) =>
          property?.wiggotMetadata && property?.wiggotMetadata?.id
            ? property?.wiggotMetadata?.id
            : ''
        )
      propertyInteraction({
        propertyLegacyIds: wiggotProperties,
        link: window.location.href,
        userId: profile?.userId?.toString() || '',
        type: 'SEARCH'
      })
    }
  }, [propertyLoaderStatus?.loading])
  return (
    <div
      className={composeClasses(
        'flex md:w-auto',
        hiddeMap ? 'w-full' : 'w-auto'
      )}
    >
      <PropertiesLoader cb={saveSearch} />
      <div
        className={composeClasses(
          'transition-all flex flex-col gap-3 p-6  h-full bg-surface-gray-10 border-r border-surface-strokes',
          showSideInfo
            ? 'translate-x-0 w-[320px] min-w-[320px]'
            : '-translate-x-[500px] w-0 min-w-[0px]',
          !showSideInfo && noDisplaySideInfo && 'hidden'
        )}
      >
        <div className="flex justify-between items-start">
          <article className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold">Información de la zona</h3>
            <div className="flex gap-1 text-sm text-secondary">
              <p>por</p>
              <Image src={MonopolioLetters} height={15} alt="monopolio" />
              <Image src={MonopolioIcon} height={15} alt="monopolio" />
            </div>
          </article>
          <button
            onClick={() => setShowSideInfo(false)}
            type="button"
            className="p-2 flex justify-center items-center"
          >
            <GoogleIcon className="text-2xl leading-6" name="close" />
          </button>
        </div>
        <p className="text-secondary text-xs">
          Resumen de las propiedades relacionadas a tu búsqueda
        </p>
        <div className="flex flex-col rounded-xl border border-strokes shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)]">
          <div className="flex flex-col gap-[2px] p-2 rounded-t-xl bg-white">
            <p className="text-xs font-semibold">
              Precio promedio de los anuncios:
            </p>
            <div className="flex items-center gap-1 text-lg font-semibold">
              <p>$</p>
              <p>{zone.avgPrice}</p>
              <p className="text-sm text-terciary">MXN</p>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-b-xl bg-surface-gray-10 p-2">
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-surface-gray-20 rounded-lg flex justify-center items-center h-fit">
                  <GoogleIcon
                    name="local_atm"
                    className="text-xl leading-5 text-[#898CA8]"
                  />
                </div>
                <div className="flex flex-col gap-[2px]">
                  <p className="text-[#484A5E] text-xs font-semibold">
                    El precio promedio por m2:
                  </p>
                  <p className="text-xs font-semibold">
                    $ {zone.avgM2Price} MXN
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1 bg-surface-gray-20 rounded-lg flex justify-center items-center h-fit">
                  <GoogleIcon
                    name="view_in_ar"
                    className="text-xl leading-5 text-[#898CA8]"
                  />
                </div>
                <div className="flex flex-col gap-[2px]">
                  <p className="text-[#484A5E] text-xs font-semibold">
                    El tamaño promedio:
                  </p>
                  <p className="text-xs font-semibold">{zone.avgSurface} m²</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Transition
        duration={150}
        animationStart="slideInLeft"
        show={!hiddeCards}
        className={composeClasses(
          '!flex justify-center overflow-y-auto 3xl:text-ellipsis',
          hiddeMap && 'w-full'
        )}
      >
        <div
          className={composeClasses(
            'px-2 sm:px-6 py-3 overflow-y-auto transition-all w-full',
            hiddeCards && 'hidden',
            !hiddeMap && 'md:w-[692px]',
            hiddeMap && !showSideInfo && 'md:w-[calc(100vw-72px)]',
            showSideInfo && 'md:w-[calc(100vw-392px)]'
          )}
        >
          <PropertyList
            flyToMapPropertyId={flyToMapPropertyId}
            onMouseEnter={(property) => {
              setIdHover(property.id)
            }}
            onMouseLeave={() => {
              setIdHover('')
              setFlyToMapPropertyId('')
            }}
            listClassName={composeClasses(
              'gap-2.5',
              hiddeMap &&
                !showSideInfo &&
                'sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6',
              !hiddeMap && !showSideInfo && 'sm:grid-cols-2',
              showSideInfo &&
                'sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5'
            )}
            skeletonClassName="h-[350px]"
            onCleanSearch={onCleanFilters}
          >
            <PropertyList.Loader>
              <GridLoader
                text={
                  showOnlyCommissionProperties
                    ? 'Explorando todas las propiedades de Wiggot'
                    : 'Explorando en todos los portales inmobiliarios'
                }
              />
            </PropertyList.Loader>

            <PropertyList.Header>
              <div
                className={composeClasses(
                  'w-full relative',
                  !hiddeMap && 'pb-[50px]'
                )}
              >
                <ListHeader
                  onChangeShowSideInfo={onChangeShowSideInfo}
                  onShowMap={onShowMap}
                  hiddeMap={hiddeMap}
                  mapboxGLIsSupported={mapboxGLIsSupported}
                />
                <div className="w-full flex mb-[10px] md:absolute z-2">
                  <ZoneInformationBySearchLoader />
                  {(!hiddeMap || !isMD) && (
                    <ZoneInformationBySearchAccordion
                      openDirection="bottom"
                      className="max-w-full !rounded-xl !bg-surface-gray-10"
                      contentClassName="border-0 tour-search-step-2-2"
                    />
                  )}
                </div>
              </div>
            </PropertyList.Header>
            <PropertyList.Card>
              <PropertyCard
                onPropertyClick={(prop) => {
                  const id =
                    prop?.wiggotMetadata && prop?.wiggotMetadata?.id
                      ? prop?.wiggotMetadata?.id
                      : prop?.itemId
                  window.open(`/search/property-detail/${id}`, '_blank')
                  if (isInitialized) {
                    trackEvent({
                      name: AmplitudeEvents.SEARCH_RESULTS_GOTO_PROPERTYDETAIL
                    })
                  }
                }}
              >
                <PropertyCard.Header>
                  <CardHeader
                    onShowMap={onShowMap}
                    onFlyToMap={onFlyToMap}
                    idHover={idHover}
                    mapboxGLIsSupported={mapboxGLIsSupported}
                    isShowTour={isShowTour}
                  />
                </PropertyCard.Header>
                <PropertyCard.Footer>
                  <CardFooter />
                </PropertyCard.Footer>
              </PropertyCard>
            </PropertyList.Card>
            <PropertyList.Paginator>
              <PropertyListPaginator className="mt-8 mb-6" />
            </PropertyList.Paginator>
          </PropertyList>
        </div>
      </Transition>
    </div>
  )
}
